import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MonitoringService} from './monitoring.service';
import {JobHistory, JobStatus} from './monitoring.models';
import {environment} from '../../environments/environment';
import {merge, of as observableOf, of, timer} from 'rxjs';
import {catchError, debounce, map, startWith, switchMap} from 'rxjs/operators';
import {GlobalConstants} from '../global-constants';
import {HttpClient} from '@angular/common/http';
import * as moment from 'moment';
import {Location} from '../tools/tools.models';
import {FormArray, FormControl} from '@angular/forms';
import {SearchFilters} from '../location/location.models';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTable} from '@angular/material/table';
import {FiltersService} from '../location/location.service';
import {ViewJobHistoryDialog} from './modals/view/view-job-history-dialog';
import {MatDialog} from '@angular/material/dialog';
import {DeleteJobHistoryDialog} from './modals/delete/delete-job-history-dialog';

@Component({
    selector: 'app-monitoring',
    templateUrl: './monitoring.component.html',
    styleUrls: ['./monitoring.component.css']
})
export class MonitoringComponent implements OnInit, OnDestroy, AfterViewInit {
    readonly LOCAL_STORAGE_FILTERS_SUFFIX = 'job-history-2';
    status: JobStatus;
    runningJobStatus = JobStatus.RUNNING;
    isLoadingResults = true;
    intervalId: any;
    lastCheck: Date;
    progress = false;
    logsUrl: String;
    project = environment.project;
    locations: Array<Location>;
    selectedLocation: Location;
    fromDate = new FormControl();
    toDate = new FormControl();
    synchronizeMappings = false;
    forceExtract = true;

    displayedColumns: string[] = ['createdAt', 'type', 'locationName', 'totalAmt', 'successAmt', 'failedAmt', 'status', 'star'];
    data: JobHistory[] = [];
    resultsLength = 0;
    pageSize = 25;
    controls: FormArray;
    filters: SearchFilters;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatTable) table: MatTable<any>;

    constructor(public route: ActivatedRoute,
                public router: Router,
                public monitoringService: MonitoringService,
                private _snackBar: MatSnackBar,
                private httpClient: HttpClient,
                private filtersService: FiltersService,
                public dialog: MatDialog,) {
        this.initFilters();
        this.monitoringService.findAllLocations().subscribe(
            locations => {
                this.locations = locations;
            },
            error => {
                console.log(error);
            });
    }

    ngOnInit(): void {
        this.intervalId = setInterval(() => {
            this.lastCheck = new Date();
            this.monitoringService.getEtlJobStatus().subscribe(
                status => {
                    this.status = status;
                },
                error => {
                    console.log(error);
                });
        }, 1000 * 5);

        let timeStamp = moment().toISOString();
        this.httpClient.get(`${GlobalConstants.apiURL}/actuator/info`, {responseType: 'json'}).pipe(catchError(error => of(error)))
            .subscribe(r => {
                    let buildNo = r.build?.build_number || 1386;
                    this.logsUrl = 'https://console.cloud.google.com/logs/query;query=resource.type%3D%22gae_app%22%0Aresource.labels.module_id%3D%22revenue%22%0Aresource.labels.version_id%3D%22' +
                        'build-' + buildNo + '%22;' +
                        'cursorTimestamp=' + timeStamp + ';duration=P1D?' +
                        'serviceId=revenue' +
                        '&versionId=build-' + buildNo +
                        '&project=' + this.project;
                    console.log(this.logsUrl);
                },
                error => {
                    console.error(error)
                });
    }

    ngOnDestroy(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    ngAfterViewInit() {
        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                debounce(() => timer(500)),
                switchMap(() => {
                    this.isLoadingResults = true;
                    return this.monitoringService.findJobHistory(this.sort.active, this.sort.direction, this.paginator.pageIndex,
                        this.paginator.pageSize, this.filtersService.buildSearchParam(this.filters))
                        .pipe(catchError(() => observableOf(null)));
                }),
                map(result => {
                    this.isLoadingResults = false;
                    // @ts-ignore
                    this.resultsLength = result?.totalElements;
                    const data = result?.content;
                    return data;
                }),
            )
            .subscribe(data => {
                this.data = data;
            });
    }

    reset() {
        this.paginator.pageIndex = 0;
        this.paginator._changePageSize(this.paginator.pageSize);
        this.monitoringService.getEtlJobStatus().subscribe(
            status => {
                this.status = status;
            },
            error => {
                console.log(error);
            });
    }

    runEtlJob() {
        this.progress = true;
        this.isLoadingResults = true;
        this.monitoringService.runEtlJob().subscribe(
            r => {
                this.reset();
                this.progress = false;
                this._snackBar.open('The ETL job was successfully triggered.', 'Close', {
                    duration: 5000,
                });
            },
            error => {
                console.log(error);
                this.progress = false;
            });
    }

    reconcileJournals() {
        this.progress = true;
        this.isLoadingResults = true;
        let request = [];
        let startDate = moment(this.fromDate.value);
        let endDate = moment(this.toDate.value);
        while (startDate <= endDate) {
            request.push({
                locationId: this.selectedLocation.vetSourceId,
                transactionDate: startDate.format('YYYY-MM-DD')
            });
            startDate = startDate.add(1, 'day');
        }
        this.monitoringService.reconcileJournals(request, this.synchronizeMappings, this.forceExtract).subscribe(
            locations => {
                this.progress = false;
                this.reset();
                this._snackBar.open('The reconciliation task has been accepted. It will run in background.', 'Close', {
                    duration: 5000,
                });
            },
            error => {
                console.log(error);
                this.progress = false;
            });
    }

    private initFilters() {
        this.filters = this.filtersService.getFilters(this.LOCAL_STORAGE_FILTERS_SUFFIX);
    }

    updateFilter() {
        this.filtersService.saveFilters(this.filters, this.LOCAL_STORAGE_FILTERS_SUFFIX);
        this.reset();
    }

    resetGrid() {
        this.filtersService.resetFilters(this.LOCAL_STORAGE_FILTERS_SUFFIX);
        this.initFilters();
        this.reset();
    }

    calculateActiveFiltersCount() {
        let result = 0;
        for (const [key, value] of Object.entries(this.filters)) {
            if (value.value) {
                result++;
            }
        }
        return result;
    }

    openViewDialog(jobHistory: JobHistory): void {
        const data = Object.assign({}, jobHistory);
        const dialogRef = this.dialog.open(ViewJobHistoryDialog, {
            width: '600px',
            data: data,
        });
    }

    openDeleteDialog(jobHistory: JobHistory): void {
        const dialogRef = this.dialog.open(DeleteJobHistoryDialog, {
            width: '550px',
            data: jobHistory,
        });

        // tslint:disable-next-line:ter-arrow-parens
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                jobHistory.loading = true;
                this.monitoringService.delete(jobHistory.id)
                    .subscribe((res: { id: any }) => {
                        // tslint:disable-next-line:no-shadowed-variable
                        this.data = this.data.filter(item => item.id !== jobHistory.id);
                        this.resultsLength -= 1;
                        this.table.renderRows()
                        this._snackBar.open('Location \'' + jobHistory.id + '\' was successfully deleted!', 'Close', {
                            duration: 5000,
                        });
                    });
            }
        });
    }
}
